import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { BigTextarea } from '@/components/BigTextarea';
import { Icon } from '@/components/Icon';
import { api } from '@/api/client';
import { haptic } from '@/lib/telegram';

const DRAFT_KEY = 'nu:campaign-draft:v1';

interface DraftState {
  name: string;
  dialogPrompt: string;
  dmReplyPrompt: string;
  scheduleStartHour: number;
  scheduleEndHour: number;
  intervalMinutes: number;
  maxChatsPerAccountPerDay: number;
  uniqueDialogPerChat: boolean;
  savedAt?: string;
}

const empty: DraftState = {
  name: '',
  dialogPrompt: '',
  dmReplyPrompt: '',
  scheduleStartHour: 10,
  scheduleEndHour: 22,
  intervalMinutes: 60,
  maxChatsPerAccountPerDay: 8,
  uniqueDialogPerChat: true,
};

export function CampaignNewPage(): JSX.Element {
  const nav = useNavigate();
  const [d, setD] = useState<DraftState>(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) return { ...empty, ...JSON.parse(raw) };
    } catch {}
    return empty;
  });
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState<'saved' | null>(null);
  const debounceRef = useRef<number | null>(null);

  // Автосейв черновика — debounce 600ms
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...d, savedAt: new Date().toISOString() }));
        setSavedFlash('saved');
        window.setTimeout(() => setSavedFlash(null), 1200);
      } catch {}
    }, 600);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [d]);

  const set = <K extends keyof DraftState>(k: K, v: DraftState[K]) => setD((x) => ({ ...x, [k]: v }));

  const create = async () => {
    if (!d.name.trim()) return;
    setSaving(true);
    haptic('success');
    const c = await api.createCampaign({
      name: d.name.trim(),
      dialogPrompt: d.dialogPrompt || undefined,
      dmReplyPrompt: d.dmReplyPrompt || undefined,
      scheduleStartHour: d.scheduleStartHour,
      scheduleEndHour: d.scheduleEndHour,
      intervalMinutes: d.intervalMinutes,
      maxChatsPerAccountPerDay: d.maxChatsPerAccountPerDay,
      uniqueDialogPerChat: d.uniqueDialogPerChat,
    });
    localStorage.removeItem(DRAFT_KEY);
    setSaving(false);
    nav(`/campaigns/${c.id}`, { replace: true });
  };

  const wipe = () => {
    if (!confirm('Очистить черновик?')) return;
    haptic('warning');
    localStorage.removeItem(DRAFT_KEY);
    setD(empty);
  };

  const filled =
    [d.name, d.dialogPrompt, d.dmReplyPrompt].filter((s) => s.trim().length > 0).length;
  const progress = Math.round((filled / 3) * 100);

  return (
    <div className="space-y-5 pb-24">
      <Topbar
        title="Новая кампания"
        subtitle="черновик сохраняется автоматически"
        right={
          <span
            className={`pill transition ${savedFlash ? 'pill-good' : ''}`}
            title={d.savedAt ? `Сохранено: ${new Date(d.savedAt).toLocaleString('ru-RU')}` : ''}
          >
            <Icon name={savedFlash ? 'check' : 'refresh'} size={12} />
            {savedFlash ? 'сохранено' : 'черновик'}
          </span>
        }
      />

      {/* Прогресс-полоска: насколько заполнено */}
      <div>
        <div className="mb-1.5 flex items-center justify-between text-2xs uppercase tracking-wider text-ink-500">
          <span>Заполнено</span>
          <span className="text-ink-100">{progress}%</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
          <div
            className="h-full rounded-full bg-grad-primary transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* 1. Название */}
      <Section
        step="01"
        title="Имя кампании"
        hint="Короткое и понятное — увидите в списке"
      >
        <input
          className="input-minimal"
          placeholder="Например, Mining pool · 44pool RU"
          value={d.name}
          onChange={(e) => set('name', e.target.value)}
          autoFocus
        />
      </Section>

      {/* 2. Бриф для диалогов */}
      <Section
        step="02"
        title="Бриф для диалогов"
        hint="Что обсуждаем · какой тон · что в финальной реплике (CTA, рефка, инвайт)"
      >
        <BigTextarea
          placeholder="Например: «Естественный разговор о выборе майнинг-пула. B хвалит 44pool за низкие комиссии и ежедневные выплаты. В последнем сообщении — рефка»"
          rows={6}
          value={d.dialogPrompt}
          onChange={(v) => set('dialogPrompt', v)}
        />
      </Section>

      {/* 3. Поведение в ЛС */}
      <Section
        step="03"
        title="Ответы в ЛС / на пинги"
        hint="Как аккаунт отвечает, когда ему пишут после посева"
      >
        <BigTextarea
          placeholder="Например: «Отвечать как опытный майнер, рефку давать только по запросу»"
          rows={5}
          value={d.dmReplyPrompt}
          onChange={(v) => set('dmReplyPrompt', v)}
        />
      </Section>

      {/* 4. Расписание + лимиты — компактно */}
      <Section step="04" title="Расписание · лимиты" hint="Можно поменять позже на экране запуска">
        <div className="grid grid-cols-2 gap-3">
          <Mini label="окно с" value={`${d.scheduleStartHour}:00`}
            onMinus={() => set('scheduleStartHour', Math.max(0, d.scheduleStartHour - 1))}
            onPlus={() => set('scheduleStartHour', Math.min(23, d.scheduleStartHour + 1))} />
          <Mini label="окно до" value={`${d.scheduleEndHour}:00`}
            onMinus={() => set('scheduleEndHour', Math.max(1, d.scheduleEndHour - 1))}
            onPlus={() => set('scheduleEndHour', Math.min(24, d.scheduleEndHour + 1))} />
          <Mini label="интервал" value={`${d.intervalMinutes} мин`}
            onMinus={() => set('intervalMinutes', Math.max(5, d.intervalMinutes - 5))}
            onPlus={() => set('intervalMinutes', Math.min(240, d.intervalMinutes + 5))} />
          <Mini label="чатов/день" value={`${d.maxChatsPerAccountPerDay}`}
            onMinus={() => set('maxChatsPerAccountPerDay', Math.max(1, d.maxChatsPerAccountPerDay - 1))}
            onPlus={() => set('maxChatsPerAccountPerDay', Math.min(30, d.maxChatsPerAccountPerDay + 1))} />
        </div>

        <label className="mt-3 flex cursor-pointer items-center justify-between rounded-2xl border border-white/8 bg-white/3 p-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink-50">Уникальный диалог в чат</div>
            <div className="text-2xs text-ink-500">Один сценарий не запускается дважды в один и тот же чат.</div>
          </div>
          <Toggle on={d.uniqueDialogPerChat} onChange={(v) => set('uniqueDialogPerChat', v)} />
        </label>
      </Section>

      {/* Sticky footer-кнопки */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-white/5 bg-black/80 px-4 pb-[max(14px,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl">
        <div className="mx-auto flex max-w-md gap-2">
          <button onClick={wipe} className="btn-ghost flex-shrink-0">
            <Icon name="x" size={14} />
          </button>
          <button
            onClick={create}
            disabled={!d.name.trim() || saving}
            className="btn-primary flex-1"
          >
            {saving ? 'Создаю…' : 'Создать кампанию'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ step, title, hint, children }: {
  step: string; title: string; hint?: string; children: React.ReactNode
}): JSX.Element {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-3">
        <span className="text-2xs font-semibold uppercase tracking-widest text-accent-300">{step}</span>
        <h3 className="text-base font-semibold text-ink-50">{title}</h3>
      </div>
      {hint && <p className="text-xs text-ink-500">{hint}</p>}
      {children}
    </section>
  );
}

function Mini({ label, value, onMinus, onPlus }: { label: string; value: string; onMinus: () => void; onPlus: () => void }): JSX.Element {
  return (
    <div>
      <div className="mb-1 text-2xs uppercase tracking-wider text-ink-500">{label}</div>
      <div className="flex items-center gap-2">
        <button onClick={onMinus} className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-ink-100 active:scale-95">−</button>
        <div className="flex-1 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-center text-sm font-semibold text-ink-50">{value}</div>
        <button onClick={onPlus} className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-ink-100 active:scale-95">+</button>
      </div>
    </div>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }): JSX.Element {
  return (
    <button
      onClick={() => { haptic('light'); onChange(!on); }}
      className={`relative h-7 w-12 shrink-0 rounded-full transition ${on ? 'bg-accent-500' : 'bg-white/10'}`}
      aria-pressed={on}
    >
      <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-soft transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
    </button>
  );
}
